import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isNativeApp } from 'utils/platformHelpers';
import { isRunningAsPWA } from 'utils/pwaHelpers';

const PwaUpdateContext = createContext({
  updateAvailable: false,
  applyUpdate: () => {},
  dismissPrompt: () => {},
  promptVisible: false,
  isPwa: false,
});

/**
 * Detects a waiting / newly installed service worker and exposes apply/dismiss.
 * Shared by UpdatePrompt snackbar and the menu Update button.
 */
export const PwaUpdateProvider = ({ children }) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const isPwa = typeof window !== 'undefined' && isRunningAsPWA() && !isNativeApp();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' || isNativeApp()) {
      return undefined;
    }
    if (!('serviceWorker' in navigator)) {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;
    let registration = null;

    const markAvailable = (worker) => {
      if (cancelled) return;
      if (worker) setWaitingWorker(worker);
      setUpdateAvailable(true);
    };

    const onControllerChange = () => {
      window.location.reload();
    };

    // Attach early so we don't miss activation during registration.update()
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    const setup = async () => {
      try {
        registration = await navigator.serviceWorker.getRegistration();
        if (!registration || cancelled) return;

        if (registration.waiting && navigator.serviceWorker.controller) {
          markAvailable(registration.waiting);
        }

        const checkUpdate = async () => {
          try {
            await registration.update();
            if (registration.waiting && navigator.serviceWorker.controller) {
              markAvailable(registration.waiting);
            }
          } catch (error) {
            console.error('Error checking for updates:', error);
          }
        };

        await checkUpdate();
        if (cancelled) return;

        intervalId = setInterval(checkUpdate, 60000);

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              markAvailable(newWorker);
            }
          });
        });
      } catch (error) {
        console.error('Error setting up update check:', error);
      }
    };

    setup();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      // controllerchange handler reloads; fallback if already controlling
      setTimeout(() => {
        window.location.reload();
      }, 500);
      return;
    }
    window.location.reload();
  }, [waitingWorker]);

  const dismissPrompt = useCallback(() => {
    setPromptDismissed(true);
  }, []);

  const value = useMemo(
    () => ({
      updateAvailable,
      applyUpdate,
      dismissPrompt,
      promptVisible: updateAvailable && !promptDismissed,
      isPwa,
    }),
    [updateAvailable, applyUpdate, dismissPrompt, promptDismissed, isPwa]
  );

  return <PwaUpdateContext.Provider value={value}>{children}</PwaUpdateContext.Provider>;
};

export const usePwaUpdate = () => useContext(PwaUpdateContext);

export default PwaUpdateProvider;
